import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Eye,
  AlertTriangle,
  CheckCircle,
  Calendar,
  Users,
  Clock,
  Building,
  MapPin,
  Filter,
  Download,
  RefreshCw,
  TrendingUp,
  TrendingDown
} from 'lucide-react';

/**
 * ImpactPreview - Shows the impact of material changes on scheduled offerings
 * 
 * Provides comprehensive view of which SOs would be affected by material updates,
 * helping admins understand the scope and timing of changes before committing.
 * 
 * Features:
 * - Affected SOs breakdown by status
 * - Timeline view of upcoming sessions
 * - Client and geographical distribution
 * - Impact severity assessment
 * - Export and notification options
 * - Support for both Session and Program level impacts
 */
const ImpactPreview = ({ 
  session, 
  impactData, 
  entityType = 'session', // 'session' | 'program'
  onCancel,
  onConfirm
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [filterStatus, setFilterStatus] = useState('all');

  if (!impactData) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Eye className="h-8 w-8 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-500">Loading impact analysis...</p>
        </CardContent>
      </Card>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    
    const formattedDate = date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
    
    if (diffDays <= 0) return `${formattedDate} (Today)`;
    if (diffDays === 1) return `${formattedDate} (Tomorrow)`;
    if (diffDays <= 7) return `${formattedDate} (${diffDays} days)`;
    return formattedDate;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'scheduled':
        return <Badge className="bg-blue-100 text-blue-800">Scheduled</Badge>;
      case 'in_progress':
        return <Badge className="bg-green-100 text-green-800">In Progress</Badge>;
      case 'completed':
        return <Badge variant="secondary">Completed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const isProgramLevel = entityType === 'program';
  const title = isProgramLevel 
    ? 'Program Materials Impact Analysis'
    : 'Session Materials Impact Analysis';
  
  const description = isProgramLevel
    ? 'Changes to Program materials will affect all Sessions that inherit from this Program'
    : 'Changes to Session materials will affect all SOs using this Session';

  const getImpactSeverity = () => {
    const { totalImpact, upcomingSessions } = impactData;
    
    if (upcomingSessions >= 10) return { level: 'high', color: 'text-red-600', bg: 'bg-red-50' };
    if (upcomingSessions >= 5) return { level: 'medium', color: 'text-amber-600', bg: 'bg-amber-50' };
    return { level: 'low', color: 'text-green-600', bg: 'bg-green-50' };
  };

  const severity = getImpactSeverity();

  // Group SOs by client for better organization
  const affectedSOs = Array.isArray(impactData.affectedSOs) ? impactData.affectedSOs : [];
  const sosByClient = affectedSOs.reduce((acc, so) => {
    if (!acc[so.clientName]) {
      acc[so.clientName] = [];
    }
    acc[so.clientName].push(so);
    return acc;
  }, {});

  // Filter SOs based on status
  const filteredSOs = affectedSOs.filter(so => 
    filterStatus === 'all' || so.status === filterStatus
  );

  return (
    <div className="space-y-6">
      {/* Impact Overview */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center space-x-2">
                <Eye className="h-5 w-5" />
                <span>Impact Preview</span>
              </CardTitle>
              <CardDescription>
                Preview the impact of material changes on scheduled offerings
              </CardDescription>
            </div>
            <div className="flex space-x-2">
              <Button variant="outline" size="sm" className="flex items-center space-x-2">
                <RefreshCw className="h-4 w-4" />
                <span>Refresh</span>
              </Button>
              <Button variant="outline" size="sm" className="flex items-center space-x-2">
                <Download className="h-4 w-4" />
                <span>Export</span>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Impact Severity Indicator */}
          <div className={`p-4 rounded-lg mb-6 ${severity.bg} border`}>
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-full ${severity.level === 'high' ? 'bg-red-100' : severity.level === 'medium' ? 'bg-amber-100' : 'bg-green-100'}`}>
                {severity.level === 'high' ? (
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                ) : severity.level === 'medium' ? (
                  <Clock className="h-5 w-5 text-amber-600" />
                ) : (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                )}
              </div>
              <div>
                <h3 className={`font-semibold ${severity.color} capitalize`}>
                  {severity.level} Impact Change
                </h3>
                <p className="text-sm text-gray-600">
                  {severity.level === 'high' 
                    ? 'This change will affect many upcoming sessions. Consider timing carefully.'
                    : severity.level === 'medium'
                    ? 'Moderate impact on scheduled sessions. Review timing.'
                    : 'Low impact change. Safe to proceed.'
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900 mb-1">
                {impactData.totalImpact}
              </div>
              <div className="text-sm text-gray-500 flex items-center justify-center space-x-1">
                <Users className="h-3 w-3" />
                <span>Total SOs</span>
              </div>
            </div>

            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-900 mb-1">
                {impactData.upcomingSessions}
              </div>
              <div className="text-sm text-blue-600 flex items-center justify-center space-x-1">
                <Calendar className="h-3 w-3" />
                <span>Upcoming</span>
              </div>
            </div>

            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-900 mb-1">
                {impactData.completedSessions}
              </div>
              <div className="text-sm text-green-600 flex items-center justify-center space-x-1">
                <CheckCircle className="h-3 w-3" />
                <span>Completed</span>
              </div>
            </div>

            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-900 mb-1">
                {Object.keys(sosByClient).length}
              </div>
              <div className="text-sm text-purple-600 flex items-center justify-center space-x-1">
                <Building className="h-3 w-3" />
                <span>Clients</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Detailed Impact Analysis */}
      <Card>
        <CardHeader>
          <CardTitle>Affected Scheduled Offerings</CardTitle>
          <CardDescription>
            Detailed breakdown of SOs that will receive the updated materials
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="timeline">Timeline</TabsTrigger>
              <TabsTrigger value="clients">By Client</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-4">
              {/* Filter Controls */}
              <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Filter className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Filter by status:</span>
                </div>
                <div className="flex space-x-2">
                  {['all', 'scheduled', 'in_progress', 'completed'].map(status => (
                    <Button
                      key={status}
                      variant={filterStatus === status ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setFilterStatus(status)}
                      className="capitalize"
                    >
                      {status === 'all' ? 'All' : status.replace('_', ' ')}
                    </Button>
                  ))}
                </div>
              </div>

              {/* SO List */}
              <div className="space-y-3">
                {filteredSOs.map((so) => (
                  <div key={so.soNumber} className="p-4 border rounded-lg hover:bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="flex items-center space-x-3 mb-1">
                          <h4 className="font-medium text-gray-900">
                            SO {so.soNumber}
                          </h4>
                          {getStatusBadge(so.status)}
                        </div>
                        <p className="text-sm text-gray-600">{so.clientName}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">
                          {formatDate(so.date)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <span className="flex items-center space-x-1">
                        <Calendar className="h-3 w-3" />
                        <span>{session.name}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <Clock className="h-3 w-3" />
                        <span>{session.duration}m session</span>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="timeline" className="space-y-4">
              <div className="space-y-4">
                {filteredSOs
                  .sort((a, b) => new Date(a.date) - new Date(b.date))
                  .map((so, index) => (
                    <div key={so.soNumber} className="relative">
                      {/* Timeline line */}
                      {index < filteredSOs.length - 1 && (
                        <div className="absolute left-6 top-16 w-0.5 h-full bg-gray-200"></div>
                      )}
                      
                      <div className="flex items-start space-x-4 p-4 bg-white border rounded-lg">
                        {/* Timeline dot */}
                        <div className="relative z-10 flex-shrink-0 w-3 h-3 bg-blue-500 rounded-full mt-2"></div>
                        
                        <div className="flex-1">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h4 className="font-medium text-gray-900">
                                SO {so.soNumber} - {so.clientName}
                              </h4>
                              <p className="text-sm text-gray-500">{formatDate(so.date)}</p>
                            </div>
                            {getStatusBadge(so.status)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </TabsContent>

            <TabsContent value="clients" className="space-y-4">
              <div className="space-y-6">
                {Object.entries(sosByClient).map(([clientName, clientSOs]) => (
                  <Card key={clientName}>
                    <CardHeader className="pb-3">
                      <div className="flex justify-between items-center">
                        <CardTitle className="text-lg">{clientName}</CardTitle>
                        <Badge variant="secondary" className="flex items-center space-x-1">
                          <Users className="h-3 w-3" />
                          <span>{clientSOs.length} SO{clientSOs.length !== 1 ? 's' : ''}</span>
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {clientSOs.map((so) => (
                          <div key={so.soNumber} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                            <div>
                              <span className="font-medium">SO {so.soNumber}</span>
                              <span className="text-sm text-gray-500 ml-2">
                                {formatDate(so.date)}
                              </span>
                            </div>
                            {getStatusBadge(so.status)}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Impact Actions */}
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>
            {description}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Impact Summary for Program Level */}
          {isProgramLevel && impactData.impact && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">Change Impact Summary</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-blue-700">Total Sessions:</span>
                  <div className="font-bold text-blue-900">{impactData.impact.totalSessions}</div>
                </div>
                <div>
                  <span className="text-blue-700">Sessions with Materials:</span>
                  <div className="font-bold text-blue-900">{impactData.impact.sessionsWithMaterials}</div>
                </div>
                <div>
                  <span className="text-blue-700">Total SOs:</span>
                  <div className="font-bold text-blue-900">{impactData.impact.totalSOs}</div>
                </div>
                <div>
                  <span className="text-blue-700">Will Inherit:</span>
                  <div className="font-bold text-blue-900">{impactData.impact.wouldInheritFromProgram}</div>
                </div>
              </div>
              {impactData.recommendations && (
                <div className="mt-3 text-sm text-blue-800">
                  <strong>Risk Level:</strong> 
                  <Badge 
                    variant="outline" 
                    className={`ml-2 ${
                      impactData.recommendations.riskLevel === 'high' ? 'border-red-400 text-red-700' :
                      impactData.recommendations.riskLevel === 'medium' ? 'border-amber-400 text-amber-700' :
                      'border-green-400 text-green-700'
                    }`}
                  >
                    {impactData.recommendations.riskLevel.toUpperCase()}
                  </Badge>
                  <p className="mt-2">{impactData.recommendations.impactDescription}</p>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" size="sm" className="flex items-center space-x-2">
              <Users className="h-4 w-4" />
              <span>Notify Affected {isProgramLevel ? 'Sessions' : 'SOs'}</span>
            </Button>
            <Button variant="outline" size="sm" className="flex items-center space-x-2">
              <Calendar className="h-4 w-4" />
              <span>Schedule Deployment</span>
            </Button>
            <Button variant="outline" size="sm" className="flex items-center space-x-2">
              <Download className="h-4 w-4" />
              <span>Export Impact Report</span>
            </Button>
            <Button variant="outline" size="sm" className="flex items-center space-x-2">
              <TrendingUp className="h-4 w-4" />
              <span>View Analytics</span>
            </Button>
          </div>

          {/* Confirm/Cancel Actions */}
          {(onCancel || onConfirm) && (
            <div className="flex justify-end space-x-3 pt-4 border-t">
              {onCancel && (
                <Button 
                  variant="outline" 
                  onClick={onCancel}
                  className="flex items-center space-x-2"
                >
                  <span>Cancel</span>
                </Button>
              )}
              {onConfirm && (
                <Button 
                  onClick={onConfirm}
                  className="flex items-center space-x-2"
                >
                  <CheckCircle className="h-4 w-4" />
                  <span>Confirm Changes</span>
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ImpactPreview;